import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Chip
} from "@mui/material";
import { getData, postData } from "../utilities/UtilityFunctions";
import { postNotification } from "../utilities/Notifications";
import GenericMultiSelect from "./GenericMultiSelect";
import { BCPSquarePlus } from "../icons/bcp/BCPIcons";

export default function EventSearchTokenTypes({
  organizationId,
  value = [],
  setFieldValue,
  name = "tokenTypes",
  label = "Token Types (Optional)",
  formControlSX = { mt: 0, mb: 0 },
  disabled = false,
  showManagement = false,
  onTokenTypesChange
}) {
  const { t } = useTranslation();
  const [tokenTypes, setTokenTypes] = useState(null);
  const [tokenDict, setTokenDict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newTokenType, setNewTokenType] = useState("");

  const fetchTokenTypes = async (orgId) => {
    if (!orgId) return;

    setLoading(true);
    try {
      const apiTokenTypes = await getData(`/gamestores/${orgId}/tokentypes`);

      if (!apiTokenTypes || !Array.isArray(apiTokenTypes)) {
        setTokenTypes([]);
        setTokenDict({});
        if (onTokenTypesChange) {
          onTokenTypesChange([], {});
        }
        return;
      }

      const tempTokens = apiTokenTypes.map((token) => token.name);
      const newTokenDict = {};
      tempTokens.forEach((token) => {
        newTokenDict[token] = {
          label: token,
          id: token
        };
      });

      setTokenTypes(tempTokens);
      setTokenDict(newTokenDict);

      if (onTokenTypesChange) {
        onTokenTypesChange(tempTokens, newTokenDict);
      }
    } catch (err) {
      if (
        err.status === 404 ||
        err.message?.includes("404") ||
        err.message?.includes("not found")
      ) {
        setTokenTypes([]);
        setTokenDict({});
      } else {
        postNotification(
          "error",
          `Failed to fetch token types: ${err.message}`
        );
        setTokenTypes([]);
        setTokenDict({});
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddTokenType = async () => {
    if (!newTokenType.trim()) {
      postNotification(
        "warning",
        t("notifications.warnings.pleaseEnterTokenTypeName")
      );
      return;
    }

    if (!organizationId) {
      postNotification(
        "error",
        t("notifications.errors.organizationIdRequired")
      );
      return;
    }

    setLoading(true);
    try {
      await postData(`/gamestores/${organizationId}/tokentypes`, {
        body: {
          name: newTokenType.trim()
        }
      });

      await fetchTokenTypes(organizationId);

      setNewTokenType("");
      postNotification(
        "success",
        `Token type "${newTokenType.trim()}" created successfully`
      );
    } catch (err) {
      postNotification(
        "error",
        `Failed to create token type: ${err.message || err}`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchTokenTypes(organizationId);
    }
    // eslint-disable-next-line
  }, [organizationId]);

  if (!showManagement && (!tokenTypes || tokenTypes.length === 0)) {
    return null;
  }

  return (
    <Box>
      {!showManagement && tokenTypes && tokenTypes.length > 0 && (
        <GenericMultiSelect
          label={label}
          name={name}
          list={loading ? ["Loading..."] : tokenTypes}
          listDict={tokenDict}
          value={value || []}
          setFieldValue={setFieldValue}
          disabled={
            disabled || loading || !tokenTypes || tokenTypes.length === 0
          }
          formControlSX={formControlSX}
        />
      )}

      {showManagement && (
        <Paper
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.default"
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: "text.primary",
              fontWeight: 600
            }}
          >
            Token Type Management
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={8} sm={9}>
              <TextField
                fullWidth
                label="New Token Type"
                value={newTokenType}
                onChange={(e) => setNewTokenType(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTokenType();
                  }
                }}
                placeholder="Enter token type name"
                disabled={loading}
                size="small"
              />
            </Grid>
            <Grid item xs={4} sm={3}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleAddTokenType}
                startIcon={<BCPSquarePlus />}
                disabled={!newTokenType.trim() || loading}
                size="small"
              >
                {loading ? "Adding..." : "Add"}
              </Button>
            </Grid>
          </Grid>

          {tokenTypes && tokenTypes.length > 0 ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: "text.secondary",
                  fontWeight: 500
                }}
              >
                Current Token Types ({tokenTypes.length})
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {tokenTypes.map((token, index) => (
                  <Chip
                    key={index}
                    label={token}
                    variant="outlined"
                    sx={{
                      backgroundColor: "background.paper",
                      borderColor: "divider",
                      color: "text.primary",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "action.hover",
                        borderColor: "primary.main"
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <Paper
              sx={{
                textAlign: "center",
                py: 3,
                border: "2px dashed",
                borderColor: "divider",
                backgroundColor: "background.paper"
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                No token types configured yet
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Add your first token type above to get started
              </Typography>
            </Paper>
          )}
        </Paper>
      )}
    </Box>
  );
}
